import { BreakpointObserver } from '@angular/cdk/layout'
import { Component, Inject, OnDestroy, OnInit } from '@angular/core'
import { MatIconRegistry } from '@angular/material/icon'
import { DomSanitizer } from '@angular/platform-browser'
import { Router } from '@angular/router'
import { combineLatest, firstValueFrom } from 'rxjs'
import { Observable } from 'rxjs/internal/Observable'
import { environment } from 'src/environments/environment'
import { RegisterIcons } from '../register-icons'
import { AnalyticsService } from '../shared/services/backend'
import { DialogService } from '../shared/services/dialog.service'
import { LoadingStateService } from '../shared/services/loading-state.service'
import { EvidaScreenNameProvider } from '../tracking/evida-screens'
import { EvidaUserIdProvider } from '../tracking/evida-user-id-provider'
import { SimpleAnalytics } from '../tracking/simple-analytics'
import { Breakpoints } from '../utils/breakpoints'
import { Logger } from '../utils/logger'
import { BASE_PATH } from './../shared/services/backend/variables'

interface AlternativeBrowser {
    iconFile: string
    downloadLink: string
}

@Component({
    selector: 'app-root',
    templateUrl: './root.component.html',
    styleUrls: ['./root.component.scss'],
    standalone: false,
})
export class RootComponent implements OnInit, OnDestroy {
    public breakpoints = [
        { class: 'xs', breakpoint: Breakpoints.xs },
        { class: 'sm', breakpoint: Breakpoints.sm_only },
        { class: 'md', breakpoint: Breakpoints.md_only },
        { class: 'lg', breakpoint: Breakpoints.lg_only },
        { class: 'xl', breakpoint: Breakpoints.xl },
    ]

    public alternativeBrowsers: AlternativeBrowser[] = [
        { iconFile: 'evida_firefox', downloadLink: 'https://www.mozilla.org/en-US/firefox/new/' },
        { iconFile: 'evida_chrome', downloadLink: 'https://www.google.com/intl/en_us/chrome/' },
        { iconFile: 'evida_opera', downloadLink: 'https://www.opera.com/' },
    ]

    public currentBreakpoint = ''

    public isLoading$!: Observable<boolean>

    constructor(
        matIconRegistry: MatIconRegistry,
        domSanitizer: DomSanitizer,
        private logger: Logger,
        private breakpointObserver: BreakpointObserver,
        private router: Router,
        private screenNameProvider: EvidaScreenNameProvider,
        @Inject(BASE_PATH) private basePath: string,
        private analytics: AnalyticsService,
        private dialogService: DialogService,
        loadingService: LoadingStateService
    ) {
        this.isLoading$ = loadingService.isLoading$
        RegisterIcons.registerIcons(matIconRegistry, domSanitizer)
    }

    private async setupSimpleAnalytics() {
        let trackingEnabled = environment.sa.enabled

        if (trackingEnabled) {
            // Enable simple analytics.
            SimpleAnalytics.enable()
            const userIdProvider = EvidaUserIdProvider.get()
            await this.setAnalyticsUserId(userIdProvider.getUserId())
            SimpleAnalytics.setup(environment.sa.appName, this.router, this.screenNameProvider, this.logger)
            SimpleAnalytics.enableUserIdentification(userIdProvider)
        } else {
            SimpleAnalytics.disable()
        }
    }

    public async ngOnInit() {
        this.setupSimpleAnalytics()
        //this.showMaintenancePopup()
        SimpleAnalytics.provideLogger(this.logger)
        SimpleAnalytics.provideScreenNameProvider(this.screenNameProvider)

        this.currentBreakpoint =
            this.breakpoints.find((item) => this.breakpointObserver.isMatched(item.breakpoint))?.class || ''

        combineLatest([
            this.breakpointObserver.observe(this.breakpoints[0].breakpoint),
            this.breakpointObserver.observe(this.breakpoints[1].breakpoint),
            this.breakpointObserver.observe(this.breakpoints[2].breakpoint),
            this.breakpointObserver.observe(this.breakpoints[3].breakpoint),
            this.breakpointObserver.observe(this.breakpoints[4].breakpoint),
        ]).subscribe((combination) => {
            const breakpointIndex = combination.findIndex((item) => item.matches)
            if (breakpointIndex >= 0) {
                this.currentBreakpoint = this.breakpoints[breakpointIndex].class
            }
        })
    }

    public ngOnDestroy(): void {
        SimpleAnalytics.tearDown()
    }

    private setAnalyticsUserId(userId: string): Promise<void> {
        const body = JSON.stringify(userId)
        return firstValueFrom(this.analytics.apiAnalyticsPost(body))
            .then(() => {})
            .catch(() => {})
    }

    private showMaintenancePopup() {
        const popupShownKey = 'maintenancePopupShown'

        if (!sessionStorage.getItem(popupShownKey)) {
            this.dialogService.openPopup({
                title: 'CutoverDialog.title',
                message: 'CutoverDialog.message',
                primaryButtonText: 'Ok',
            })
            sessionStorage.setItem(popupShownKey, 'true')
        }
    }
}
